<template>
  <b-modal
    :id="id"
    :title="isEdit? 'Edit Periode Tanam' : 'Tambah Periode Tanam'"
    centered
    hide-footer
    @shown="initModal"
  >
    <div class="fw-bold">
      Petak: {{ blockDetail.name }}
    </div>
    <hr>
    <b-form
      @submit.prevent="onSubmit"
    >
      <b-form-group
        class="mb-3 required"
        label="Luas Tanam"
        label-for="input-area"
        description="Unit satuan: Hektar (ha)"
      >
        <b-form-input
          id="input-area"
          type="number"
          placeholder="Enter area"
          required
          min="0.01"
          step="0.01"
          :value="form.area"
          @input="form.area = Math.abs($event)"
          @keydown.native="handleInput($event)"
        />
      </b-form-group>
      <div class="d-md-flex justify-content-between gap-3">
        <b-form-group
          class="mb-3 required w-100"
          label="Tanggal Tanam"
          label-for="input-start-date"
        >
          <date-picker
            id="input-start-date"
            v-model="start_date"
            :clearable="false"
            input-class="form-control"
            format="DD/MM/YYYY"
            popup-class="mx-datepicker-popup-modal"
            placeholder="dd/mm/yyyy"
          />
          <small
            v-if="errorDate"
            class="text-danger"
          >Tanggal Tanam wajib diisi!</small>
        </b-form-group>
        <b-form-group
          v-if="isEdit"
          class="mb-3 w-100"
          label="Tanggal Panen"
          label-for="input-end-date"
        >
          <date-picker
            id="input-end-date"
            v-model="end_date"
            :clearable="false"
            input-class="form-control"
            format="DD/MM/YYYY"
            popup-class="mx-datepicker-popup-modal"
            placeholder="dd/mm/yyyy"
          />
        </b-form-group>
      </div>
      <b-form-group
        class="mb-3 required"
        label="Kategori"
        label-for="input-plant-category"
      >
        <b-form-select
          id="input-plant-category"
          v-model="form.plant_category"
          required
          plain
          :options="plantCategories"
        >
          <template #first>
            <b-form-select-option
              :value="null"
              disabled
            >
              -- Pilih --
            </b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>
      <b-form-group
        v-if="isEdit"
        class="mb-3"
        label="Hasil Panen"
        label-for="input-yield"
        description="Unit satuan: Ton"
      >
        <b-form-input
          id="input-yield"
          type="number"
          min="0"
          step="0.01"
          :value="form.yield"
          @input="form.yield = Math.abs($event)"
          @keydown.native="handleInput($event)"
        />
      </b-form-group>

      <div class="d-flex justify-content-between gap-2">
        <b-button
          variant="outline-danger"
          @click="resetForm"
        >
          Reset
        </b-button>
        <div class="d-flex">
          <b-button
            type="submit"
            :variant="isNextPlantingEnabled ? 'outline-primary' : 'primary'"
            class="me-2"
          >
            Simpan
          </b-button>
          <b-form
            v-if="isEdit && isNextPlantingEnabled"
            :id="isDisabled ? 'disabled-save-plant' : ''"
            @submit.prevent="finishBlockPeriod"
          >
            <b-button
              type="submit"
              :disabled="isDisabled"
              variant="primary"
            >
              Simpan dan Tanam {{ nextCategory }}
            </b-button>
            <b-popover
              target="disabled-save-plant"
              triggers="click"
              placement="bottom"
              custom-class="bg-dark-gray"
            >
              <div class="bg-dark-gray fw-bold">
                Silahkan lengkapi tanggal panen dan hasil panen terlebih dahulu.
              </div>
            </b-popover>
          </b-form>
        </div>
      </div>
    </b-form>
  </b-modal>
</template>

<style scoped>
#disabled-save-plant {
  cursor: no-drop;
}
</style>

<script>
import DatePicker from 'vue2-datepicker';
import { format } from 'date-fns';
import { isEmptyObject, plantCategories, handleInput } from '@/utils/helper';

export default {
  components: {
    DatePicker,
  },
  props: {
    id: {
      type: String,
      default: 'modal',
    },
    blockDetail: {
      type: Object,
      required: true,
    },
    periodDetail: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      form: {
        area: '',
        start_date: '',
        end_date: '',
        plant_category: null,
        yield: null,
      },
      errorDate: false,
      start_date: null,
      end_date: null,
    };
  },
  computed: {
    isEdit() {
      return !isEmptyObject(this.periodDetail);
    },
    isNextPlantingEnabled() {
      // latest history, end_date null, not RC6
      if (this.periodDetail.id === this.blockDetail?.last_period?.id && this.form.plant_category !== 'rc6' && this.periodDetail.end_date === null) return true;
      return false;
    },
    isDisabled() {
      return !(this.form.end_date && this.form.yield);
    },
    nextCategory() {
      switch (this.form?.plant_category) {
        case 'pc': return 'RC 1';
        case 'rc1': return 'RC 2';
        case 'rc2': return 'RC 3';
        case 'rc3': return 'RC 4';
        case 'rc4': return 'RC 5';
        case 'rc5': return 'RC 6';
        default: return 'Periode Berikutnya';
      }
    },
    plantCategories() {
      return Object.entries(plantCategories).map(([value, text]) => ({ value, text }));
    },
  },
  watch: {
    start_date() {
      if (this.start_date) {
        this.form.start_date = format(this.start_date, 'yyyy-MM-dd');
        this.errorDate = false;
      }
    },
    end_date() {
      if (this.end_date) {
        this.form.end_date = format(this.end_date, 'yyyy-MM-dd');
      }
    },
  },
  methods: {
    handleInput,
    onSubmit() {
      if (this.form.start_date) { this.createBlockPeriod(); } else this.errorDate = true;
    },
    resetForm() {
      Object.keys(this.form).forEach((key) => {
        this.form[key] = null;
      });
      this.start_date = null;
      this.end_date = null;
    },
    async createBlockPeriod() {
      try {
        if (this.isEdit) {
          await this.$api.updateBlockPeriod(this.blockDetail.id, this.periodDetail.id, {
            ...this.form,
          });
          this.$emit('update-data', 'Edited');
        } else {
          await this.$api.createBlockPeriod(this.blockDetail.id, {
            ...this.form,
          });
          this.$emit('update-data', 'Saved');
        }
        this.$emit('reload-data');
        this.closeModal();
      } catch (error) {
        console.log(error);
      }
    },
    async finishBlockPeriod() {
      try {
        await this.$api.finishBlockPeriod(this.blockDetail.id, this.periodDetail.id, {
          ...this.form,
        });
        this.$emit('update-data', 'Saved');
        this.$emit('reload-data');
        this.closeModal();
      } catch (error) {
        console.log(error);
      }
    },
    closeModal() {
      this.$bvModal.hide(this.id);
      this.resetForm();
    },
    initModal() {
      if (this.isEdit) {
        this.form = { ...this.periodDetail };
        if (this.periodDetail.start_date) { this.start_date = new Date(this.periodDetail.start_date); }
        if (this.periodDetail.end_date) { this.end_date = new Date(this.periodDetail.end_date); }
      } else {
        this.resetForm();
      }
    },
  },
};
</script>

<style>

</style>
